// TestimonialsPage.jsx
import React from "react";
import { motion } from "framer-motion";
import Testimonials from "../components/Testimonials/Testimonials";
import LinkedInTestimonials from "../components/Testimonials/LinkedInTestimonials";
import { writtenTestimonials, linkedinTestimonials } from "../components/Testimonials/testimonialsData";
import { FaLinkedin } from "react-icons/fa";
import SEO from "../components/common/SEO";

const TestimonialsPage = () => {
  const testimonialsStructuredData = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "name": "Professional Testimonials & Recommendations - Mihir Shah",
    "description": "Professional testimonials and recommendations for Mihir Shah's cybersecurity expertise, OWASP VXDF leadership, and security consulting work.",
    "url": "https://mihirshah.tech/testimonials",
    "mainEntity": {
      "@type": "Person",
      "name": "Mihir Shah",
      "hasOccupation": {
        "@type": "Occupation",
        "name": "Security Engineer & OWASP Project Leader"
      },
      "review": writtenTestimonials.slice(0, 5).map(testimonial => ({
        "@type": "Review",
        "reviewBody": testimonial.testimonial,
        "author": {
          "@type": "Person",
          "name": testimonial.name
        },
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        }
      }))
    }
  };

  return (
    <>
      <SEO
        title="Professional Testimonials & Recommendations - Mihir Shah"
        description="Read professional testimonials and recommendations for Mihir Shah's cybersecurity expertise, OWASP VXDF project leadership, security consulting, and speaking engagements from industry leaders and clients."
        keywords="Mihir Shah testimonials, cybersecurity recommendations, OWASP expert reviews, security consultant testimonials, professional references, client feedback, industry endorsements"
        url="https://mihirshah.tech/testimonials"
        type="website"
        section="Testimonials"
        tags={["Testimonials", "Recommendations", "Professional References", "Client Reviews"]}
        structuredData={testimonialsStructuredData}
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-vxdf-primary to-vxdf-accent bg-clip-text text-transparent"
            >
              Professional Testimonials
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-gray-300 max-w-3xl mx-auto"
            >
              What industry leaders, clients, and colleagues say about my
              cybersecurity expertise, OWASP leadership, and professional contributions
            </motion.p>
          </div>

          {/* LinkedIn Recommendations */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mb-20"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                LinkedIn Recommendations
              </h2>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                Professional endorsements from colleagues highlighting my
                cybersecurity work experience and collaborative approach
              </p>
            </div>
            <LinkedInTestimonials linkedinPosts={linkedinTestimonials} />
          </motion.section>

          {/* Professional Testimonials Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mb-20"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Client & Professional Recommendations
            </h2>
            <Testimonials />
          </motion.section>

          {/* Call to Action */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center"
          >
            <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 backdrop-blur-xl border border-white/20 rounded-2xl p-12 max-w-4xl mx-auto">
              <h3 className="text-3xl font-bold text-white mb-6">
                Ready to Work Together?
              </h3>
              <p className="text-xl text-gray-100 mb-8 max-w-2xl mx-auto">
                Join the professionals who have experienced excellence in
                cybersecurity consulting and OWASP project collaboration
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300"
                >
                  Start a Conversation
                </a>
                <a
                  href="https://linkedin.com/in/mihir-shah99"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-xl border border-white/20 text-white font-medium rounded-lg hover:bg-white/20 transition-all duration-300"
                >
                  <FaLinkedin />
                  Connect on LinkedIn
                </a>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </>
  );
};

export default TestimonialsPage;
